/**
 * Small muted timestamp for a chat message bubble — shared by every Merlin
 * door (Quality Co-Pilot, Tech Support Co-Pilot, Analyst, Agent Console) so
 * message times render the same everywhere.
 *
 * Display follows the portal convention (browser-local timezone) but is
 * CALENDAR-day aware, unlike the email-list `formatTimeAgo` (whose rolling
 * 24-hour bucket would show a yesterday-evening message as a bare "7:10 PM"
 * — exactly the ambiguity message timestamps exist to remove):
 *   - today      → "3:45 PM"
 *   - yesterday  → "Yesterday 7:10 PM"
 *   - older      → "Jul 12, 2026, 3:30 PM"
 * The full local date-time always rides the tooltip. Renders nothing without
 * a usable timestamp (e.g. a turn still streaming).
 */
import { formatDateTime } from '../../utils/dateFormatters';
import styles from './MessageTimestamp.module.css';

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Pure formatter (exported for tests): calendar-aware short message time. */
export function formatMessageTime(dateStr: string, now?: Date): string {
  // Naive ISO strings from the API are UTC by backend convention — normalize
  // so the browser doesn't parse them as local time (mirrors parseDate).
  const hasZone = /(?:Z|[+-]\d\d:?\d\d)$/.test(dateStr);
  const date = new Date(hasZone ? dateStr : `${dateStr}Z`);
  if (isNaN(date.getTime())) return dateStr;
  const reference = now ?? new Date();
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (sameLocalDay(date, reference)) return time;
  const yesterday = new Date(reference);
  yesterday.setDate(reference.getDate() - 1);
  if (sameLocalDay(date, yesterday)) return `Yesterday ${time}`;
  return formatDateTime(dateStr);
}

export function MessageTimestamp({ at }: { at?: string | null }) {
  if (!at) return null;
  return (
    <time className={styles.stamp} dateTime={at} title={formatDateTime(at)}>
      {formatMessageTime(at)}
    </time>
  );
}
